import React, { useState } from "react";
import PropTypes from "prop-types";
import { applyLocaleToUrl } from "@/utils/formatters";
import { useRouter } from "next/router";
import ImageTitleContentCenter from "@/components/CustomComponents/ImageTileContentCenter";

const ImageTileGridSection = ({ fields }) => {
  const {
    heading = "",
    subHeading = "",
    noOfColumns = 3,
    showBorder = false,
    imageTileCards: items = [],
    backgroundColor = "white",
    componentType = "Tile Grid",
  } = fields;
  const [activeItem, setActiveItem] = useState(null);
  const { locale } = useRouter();
  const handleVideoClick = item => {
    if (item?.fields?.videoUrl) {
      setActiveItem(item);
    }
  };

  if (componentType === "Tile Center") {
    return <ImageTitleContentCenter fields={fields} />;
  }

  return (
    <div
      className={`w-full px-[20px] pt-[80px] pb-[60px] md:px-[calc((90%-980px)/2)] font-[HelveticaRoman,Helvetica,sans-serif] ${backgroundColor === "black" ? "bg-black text-white" : "bg-white text-black"} ${showBorder ? "border-b-[10px] border-white" : ""}`}
    >
      {heading && (
        <h2
          className={`text-center text-[30px] leading-[36px] ${subHeading ? "" : "mb-10"} font-normal`}
        >
          {heading}
        </h2>
      )}
      {subHeading && (
        <h2 className="text-center text-[#fff] text-[25px] leading-[36px] font-[HelveticaLight, Helvetica, sans-serif] mb-[20px] font-normal">
          {subHeading}
        </h2>
      )}
      <div
        className={`grid gap-5 grid-cols-1 ${
          noOfColumns ? `md:grid-cols-${noOfColumns}` : ""
        }`}
      >
        {items.map((item, index) => {
          const isVideo = item?.fields?.videoUrl;
          const isLink = item?.fields?.src;

          return (
            <div key={index}>
              {/* IMAGE */}
              {isLink ? (
                <div
                  className="block cursor-pointer"
                  onClick={e => {
                    const url = item?.fields?.src;
                    if (url) {
                      e.preventDefault();
                      const tabKey = url.split("/").pop();
                      // concealedshower / showercolumn / handshower
                      window.dispatchEvent(
                        new CustomEvent("changeTab", {
                          detail: tabKey,
                        })
                      );
                      document
                        .getElementById("content-tabs")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  <div className="relative group cursor-pointer">
                    <img
                      src={item?.fields?.imageUrl}
                      alt={item?.fields?.title}
                      className="w-full h-auto object-contain bg-white"
                    />
                  </div>
                </div>
              ) : isVideo ? (
                <div
                  className="relative group cursor-pointer"
                  onClick={() => handleVideoClick(item)}
                >
                  <img
                    src={item?.fields?.imageUrl}
                    alt={item?.fields?.title}
                    className="w-full h-auto object-cover"
                  />

                  {/* Overlay for video */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 transition" />
                </div>
              ) : (
                <img
                  src={item?.fields?.imageUrl}
                  alt={item?.fields?.title}
                  className="w-full h-auto object-cover"
                />
              )}

              {/* TITLE */}
              <p className="pt-6 text-[24px] leading-[28px] font-normal">
                {item?.fields?.title}
              </p>

              {item?.fields?.cta?.fields?.visible && (
                <a
                  href={applyLocaleToUrl(
                    item?.fields?.cta?.fields?.url,
                    locale
                  )}
                  onClick={e => {
                    const url = item?.fields?.cta?.fields?.url;

                    if (url?.includes("/ibt-campaign/")) {
                      e.preventDefault();
                      const tabKey = url.split("/").pop(); // suites / trends / social
                      window.dispatchEvent(
                        new CustomEvent("changeTab", {
                          detail: tabKey,
                        })
                      );
                      document
                        .getElementById("content-tabs")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="pt-4 block text-[#337ab7] cursor-pointer"
                >
                  {item.fields.cta.fields.label}
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* VIDEO MODAL */}
      {activeItem && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setActiveItem(null)}
          />

          <div className="relative z-10 w-[90%] max-w-4xl bg-white rounded-lg overflow-hidden max-h-[90vh] flex flex-col">
            <button
              onClick={() => setActiveItem(null)}
              className="absolute top-3 right-3 text-black text-2xl leading-none hover:opacity-70 z-20"
            >
              ✕
            </button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-5 flex flex-col items-center text-center">
              {/* Video */}
              <div className="w-full h-full aspect-video rounded-lg bg-black mb-6">
                <iframe
                  className="w-full h-full rounded-lg"
                  src={
                    activeItem?.fields?.videoUrl?.includes("watch?v=")
                      ? activeItem.fields.videoUrl.replace(
                          "watch?v=",
                          "embed/"
                        ) + "?rel=0"
                      : activeItem?.fields?.videoUrl
                  }
                  title="YouTube video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {activeItem?.fields?.videoText && (
                <p className="text-[16px] leading-[22px] text-black">
                  {activeItem?.fields?.videoText}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================
   PROP TYPES
========================= */

const ItemShape = PropTypes.shape({
  fields: PropTypes.shape({
    title: PropTypes.string,
    imageUrl: PropTypes.string,
    src: PropTypes.string,
    videoUrl: PropTypes.string,
    videoText: PropTypes.string,
  }),
});

ImageTileGridSection.propTypes = {
  fields: PropTypes.shape({
    heading: PropTypes.string,
    noOfColumns: PropTypes.number,
    imageTileCards: PropTypes.arrayOf(ItemShape),
    componentType: PropTypes.string,
  }),
};

/* =========================
   DEFAULT PROPS
========================= */

ImageTileGridSection.defaultProps = {
  fields: {
    heading: "",
    noOfColumns: 3,
    imageTileCards: [],
    componentType: "imageTileGrid",
  },
};

export default ImageTileGridSection;
